import React from 'react';
import styles from './WorkoutDetailsDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import type { SessionStatistic, ExerciseSetStatistic } from '../../types';
import { useSessionData } from '../../providers';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip, Paper } from '@mui/material';
import {
  calculateConcentricCompliance,
  calculateEccentricCompliance,
} from '../../utils';

interface WorkoutDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workout: SessionStatistic;
}

const calculateTimeUnderTension = (statistic: ExerciseSetStatistic) => {
  const { analyzedMetrics } = statistic;
  if (!analyzedMetrics) return '-';
  const timeUnderTension = Object.values(analyzedMetrics).reduce(
    (acc, curr) => acc + curr.upDuration + curr.downDuration,
    0,
  );
  return `${timeUnderTension.toFixed(1)}s`;
};

const formatMetricRange = (
  min: number | undefined,
  max: number | undefined,
  metricType: string,
) => {
  if (typeof min !== 'number' || typeof max !== 'number') {
    return '-';
  }
  const unit = metricType === 'Tempo' ? 's' : ' m/s';
  return `${min.toFixed(1)}${unit} - ${max.toFixed(1)}${unit}`;
};

const formatMetricValue = (value: number | undefined, metricType: string) => {
  if (typeof value !== 'number') {
    return '-';
  }
  const unit = metricType === 'Tempo' ? 's' : ' m/s';
  return `${(Math.floor(value * 10) / 10).toFixed(1)}${unit}`;
};

const RepDetailsTooltip: React.FC<{
  set: ExerciseSetStatistic;
  isConcentric: boolean;
}> = ({ set, isConcentric }) => {
  const { analyzedMetrics, metrics } = set;
  if (!analyzedMetrics || !metrics) return null;

  const planeMetrics = isConcentric ? metrics.concentric : metrics.eccentric;
  if (!planeMetrics) {
    return null;
  }

  const metricType = planeMetrics.type ?? 'Tempo';
  const targetRange = formatMetricRange(
    planeMetrics.min,
    planeMetrics.max,
    metricType,
  );
  const headerLabel = metricType === 'Tempo' ? 'TEMPO' : 'VELOCITY';

  return (
    <Paper className={styles.tooltipContent} elevation={0}>
      <table className={styles.tooltipTable}>
        <thead>
          <tr>
            <th>REP #</th>
            <th>TARGET {headerLabel}</th>
            <th>ACTUAL {headerLabel}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(analyzedMetrics).map(([repIndex, metric], index) => {
            const actual = isConcentric
              ? metricType === 'Tempo'
                ? metric.upDuration
                : metric.upVelocity
              : metricType === 'Tempo'
                ? metric.downDuration
                : metric.downVelocity;
            const numericRepIndex = Number(repIndex);
            const displayRepNumber = Number.isNaN(numericRepIndex)
              ? index + 1
              : numericRepIndex || index + 1;
            return (
              <tr key={repIndex}>
                <td>{displayRepNumber}</td>
                <td>{targetRange}</td>
                <td>{formatMetricValue(actual, metricType)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Paper>
  );
};

const WorkoutDetailsDialog: React.FC<WorkoutDetailsDialogProps> = ({
  isOpen,
  onClose,
  workout,
}) => {
  const { mergedExercisesMap } = useSessionData();

  const renderSetRow = (
    exerciseId: string,
    set: ExerciseSetStatistic,
    setNumber: number,
  ) => {
    const exercise = mergedExercisesMap[exerciseId];
    const exerciseName = exercise?.name || exerciseId;
    const concentricCompliance = calculateConcentricCompliance(set);
    const eccentricCompliance = calculateEccentricCompliance(set);
    const timeUnderTension = calculateTimeUnderTension(set);
    const weight =
      typeof set.performedWeight === 'number'
        ? `${set.performedWeight} lbs`
        : '-';
    const hasDetailedMetrics =
      Boolean(set.analyzedMetrics) && Boolean(set.metrics);

    const renderComplianceColumn = (
      complianceValue: string | null,
      isConcentric: boolean,
    ) => (
      <div className={styles.setColumn}>
        {complianceValue || '-'}
        {complianceValue && complianceValue !== '-' && hasDetailedMetrics && (
          <Tooltip
            title={<RepDetailsTooltip set={set} isConcentric={isConcentric} />}
            placement="bottom-start"
            PopperProps={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    tether: false,
                    rootBoundary: 'viewport',
                  },
                },
              ],
            }}
          >
            <InfoIcon className={styles.informationIcon} />
          </Tooltip>
        )}
      </div>
    );

    return (
      <div className={styles.setRow} key={`${exerciseId}-${setNumber}`}>
        <div className={styles.setColumn}>{exerciseName}</div>
        <div className={styles.setColumn}>{setNumber}</div>
        <div className={styles.setColumn}>{set.performedReps || '-'}</div>
        <div className={styles.setColumn}>{weight}</div>
        <div className={styles.setColumn}>
          {set.performedRest
            ? `${(set.performedRest / 1000).toFixed(0)} sec`
            : '-'}
        </div>
        <div className={styles.setColumn}>{timeUnderTension || '-'}</div>
        {renderComplianceColumn(eccentricCompliance, false)}
        {renderComplianceColumn(concentricCompliance, true)}
      </div>
    );
  };

  const renderContent = () => {
    if (!workout.exercises) {
      return (
        <div className={styles.emptyState}>No exercise data available</div>
      );
    }

    return (
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.icon}>
            {/* add here weight lifting icon */}
          </div>
          <FitnessCenterIcon className={styles.icon} />
          <div className={styles.subtitle}>{workout.name}</div>
        </div>
        <div className={styles.tableHeader}>
          <div className={styles.setColumnHeader}>EXERCISE</div>
          <div className={styles.setColumnHeader}>SET #</div>
          <div className={styles.setColumnHeader}>REPS</div>
          <div className={styles.setColumnHeader}>WEIGHT</div>
          <div className={styles.setColumnHeader}>REST TIME</div>
          <div className={styles.setColumnHeader}>TUT</div>
          <div className={styles.setColumnHeader}>ECCENTRIC COMPLIANCE</div>
          <div className={styles.setColumnHeader}>CONCENTRIC COMPLIANCE</div>
        </div>
        <div className={styles.tableBody}>
          {Object.entries(workout.exercises).map(([exerciseId, sets]) =>
            sets.map((set, index) => renderSetRow(exerciseId, set, index + 1)),
          )}
        </div>
      </div>
    );
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Details"
      children={renderContent()}
      actions={<div />}
    />
  );
};

export default WorkoutDetailsDialog;
